/**
 * ViewportDetector hook: reports size changes to the LiveView so it can
 * decide how the sidebar should behave (mobile vs desktop).
 *
 * Two things make this less trivial than a resize listener.
 *
 * **The first report can be dropped.** A push made before this view's channel
 * has joined — which is exactly where `mounted()` runs — is rejected by
 * LiveView. The server's default viewport is `:mobile`, so a dropped first
 * report leaves a full-width panel believing it is a phone: hiding the chat
 * sidebar then only translates it off-screen instead of collapsing its width,
 * and the conversation never reclaims the space. Nothing corrects that until the
 * next real resize, which is why "resize the window and it fixes itself" was the
 * symptom. So the first report retries until one actually lands.
 *
 * The same hole reopens on a **rejoin**: the server process is re-mounted with
 * its default `:mobile` viewport and has no memory of what we told the previous
 * one, so `reconnected()` restarts the same retrying report rather than firing a
 * single unretried push.
 *
 * **The panel is not the window.** Brainbox runs in a side panel whose width
 * changes when the user drags the divider, or when Wavebox opens it at a set
 * fraction — neither of which is guaranteed to raise `window.resize` in here. A
 * ResizeObserver on the document element catches the size actually changing,
 * whatever moved it.
 *
 * ## Why not `safePushEvent`
 *
 * That helper is documented for pushes "whose loss is harmless", and points
 * anything that needs the outcome at `pushEvent` directly. This is that case:
 * the first report is precisely the one whose loss is NOT harmless, and its
 * `liveSocket.isConnected()` check can't tell us whether a push landed — it
 * tests the transport, while LiveView rejects on the view's own channel, and
 * Brainbox reconnects the socket during auth so there is a real window where the
 * transport is up and the channel has not joined. We push directly and use the
 * returned promise — which settles on the server's ack — as the "landed" signal,
 * swallowing the rejection ourselves exactly as the helper would have.
 */

// How hard to try landing the first report before giving up. The channel joins
// in tens of milliseconds normally; this covers a slow connect without spinning
// forever on a view that is never going to join.
const INITIAL_RETRY_MS = 150
const INITIAL_RETRY_ATTEMPTS = 20

const ViewportDetector = {
  mounted () {
    this.resizeFrame = null
    this.retryTimer = null
    this.stopped = false

    this.startInitialReporting()

    this.handleResize = () => {
      if (this.resizeFrame) {
        cancelAnimationFrame(this.resizeFrame)
      }

      this.resizeFrame = requestAnimationFrame(() => {
        // Cleared HERE, not in pushDimensions: this is the only place that
        // knows the frame has run. Clearing it on any push (a retry, say) left
        // handleResize unable to cancel the frame it had scheduled and
        // destroyed() unable to cancel it at all.
        this.resizeFrame = null
        this.pushDimensions()
      })
    }

    window.addEventListener('resize', this.handleResize)

    // Catches panel drags and programmatic width changes, which don't reliably
    // raise window.resize inside a side panel.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.handleResize)
      this.resizeObserver.observe(document.documentElement)
    }
  },

  reconnected () {
    // A rejoin gets a freshly-mounted server process on its `:mobile` default,
    // so this is a first report all over again — counters and all, or a dropped
    // push here reproduces the bug this hook exists to fix.
    this.startInitialReporting()
  },

  destroyed () {
    this.stopped = true

    if (this.resizeFrame) {
      cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = null
    }

    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
  },

  /** Reports until one lands, from a fresh attempt budget. */
  startInitialReporting () {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }

    this.stopped = false
    this.attemptsLeft = INITIAL_RETRY_ATTEMPTS
    this.attemptInitialReport()
  },

  attemptInitialReport () {
    this.attemptsLeft -= 1

    this.pushDimensions().then((landed) => {
      // Landed: the server has the real size and the resize listeners carry it
      // from here. Otherwise try again — a rejection means the channel wasn't
      // ready, which is a matter of milliseconds, not a permanent failure.
      if (landed || this.stopped || this.attemptsLeft <= 0) {
        return
      }

      this.retryTimer = setTimeout(() => {
        this.retryTimer = null
        this.attemptInitialReport()
      }, INITIAL_RETRY_MS)
    })
  },

  /**
   * Sends one report. Resolves true only once the server has acknowledged it;
   * false for anything else (socket down, channel not joined, hook detached),
   * all of which are retryable and none of which should surface as an unhandled
   * rejection.
   */
  pushDimensions () {
    if (this.stopped || this.isMounted === false) {
      return Promise.resolve(false)
    }

    // Cheap fast path only — NOT proof the push will be accepted.
    if (!this.liveSocket || !this.liveSocket.isConnected()) {
      return Promise.resolve(false)
    }

    try {
      const pushed = this.pushEvent('viewport_change', {
        width: window.innerWidth,
        height: window.innerHeight
      })

      if (!pushed || typeof pushed.then !== 'function') {
        // A LiveView old enough to return a ref rather than a promise: no
        // landed signal available, so take the push at its word.
        return Promise.resolve(true)
      }

      return pushed.then(
        () => true,
        () => false
      )
    } catch {
      // `__view()` throws once the hook has been destroyed. Benign.
      return Promise.resolve(false)
    }
  }
}

export default ViewportDetector
